import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

export const userStudiesGqlString = `            
    userStudiesRaw {
        pk
        sk
        data
        tn
        name
        version
        studyName
        userId
        studyId
        userStudySitesRaw {
            pk
            sk
            data
            tn
            name
            version
            siteName
            userId
            studyId
            siteId
            siteIdentifier
        }
        roleVClaim {
            sk
            fxId
            fxRoleName
            name
        }
    }
`;

export const getUserPermissionsAndStudySites = gql`
    query getUserPermissionsAndStudySites($pk: String) {
        getUser(pk: $pk, sk: "User") {
            name
            displayName
            pk
            firstName
            lastName
            sk
            tn
            status
            acceptedVersionPP
            subType
            type
            parentId
            oldUserId
            roleName
            createdAt
            email
            securityQuestion1Text
            securityQuestion2Text
            securityQuestion3Text
            isDeleted
            version
            dateOfLastPasswordChange
            guidesStatus {
                pk
                sk
                isCompleted
                guideName
                updatedAt
            }
            publicKeys {
                deviceId
                key
            }
            roleBClaim {
                tn
                data
                name
                sk
                fxId
                schema
                createdAt
                createdBy
                updatedAt
                updatedBy
            }
            roleVClaim {
                tn
                data
                name
                sk
                fxId
                fxRoleName
                schema
                createdAt
                createdBy
                updatedAt
                updatedBy
            }
            lastPagesOpened {
                url
                studyId
                siteId
            }
            ${userStudiesGqlString}
            preferredTimezone
            currentUserSubscriber {
                currentRoleId
                roleVClaims {
                    sk
                    fxId
                    fxRoleName
                    name
                }
                roleBClaims {
                    sk
                    fxId
                    fxRoleName
                    name
                }
            }
        }
        getPermissionsByUser(userId: $pk) {
            items
        }
    }
`;

export const getPatientData = gql`
    query GetPatientDataQuery($pk: String!) {
        getPatient(pk: $pk) {
            name
            pk
            sk
            tn
            firstName
            lastName
            email
            type
            subType
            userStatus
            initials
            version
            isDeleted
            securityQuestion1Text
            securityQuestion2Text
            securityQuestion3Text
            acceptedVersionPP
            screeningIdentifier
            createdAt
            parentId
            studyInstanceId
            armId
            studyId
            siteId
            status
            firstDay
            patientIdentifier
            preferredLanguage
            preferredTimezone
            activationDate
            caregivers {
                pk
                caregiverNumber
                caregiverRelationshipId
                caregiverNumber
                activationCode
                isLAR
                patientICareForId
                isPrimaryCaregiver
            }
        }
        getUser(pk: $pk, sk: "User") {
            roleBClaim {
                tn
                data
                name
                schema
                sk
                fxId
                createdAt
                createdBy
                updatedAt
                updatedBy
            }
            roleVClaim {
                tn
                data
                name
                sk
                fxId
                schema
                createdAt
                createdBy
                updatedAt
                updatedBy
            }
            dateOfLastPasswordChange
            roleName
            status
        }
        getPermissionsByUser(userId: $pk) {
            items
        }
    }
`;

export const updateUserLastPasswordChanged = gql`
    mutation updateUser(
        $pk: String!
        $sk: String
        $isDeleted: Int!
        $expectedVersion: Int!
        $dateOfLastPasswordChange: AWSDateTime
    ) {
        updateUser(
            input: {
                pk: $pk
                sk: $sk
                isDeleted: $isDeleted
                expectedVersion: $expectedVersion
                dateOfLastPasswordChange: $dateOfLastPasswordChange
            }
        ) {
            pk
            sk
            version
            dateOfLastPasswordChange
        }
    }
`;

export const switchUserRoleDocumentNode = gql`
    mutation SwitchSubscriberAndRole($userId: String!, $subscriberId: String!, $roleId: String!, $fxRoleId: String) {
        switchSubscriberAndRole(userId: $userId, subscriberId: $subscriberId, roleId: $roleId, fxRoleId: $fxRoleId)
    }
`;

export const getCurrentPolicyAPI = gql`
    query listSubPP($pk: String!, $filter: SearchableSubscriberPPFilterInput) {
        listSubscriberPPs(pk: $pk, filter: $filter) {
            items {
                pk
                sk
                language
                majorVersion
                version
                versionStr
                updatedAt
                updatedByName {
                    firstName
                    lastName
                    username
                }
            }
        }
    }
`;

export const getSubscriberQuery = gql`
    query getsubscriber($pk: String!) {
        getSubscriber(pk: $pk) {
            allowDcf
            name
        }
    }
`;

export const updateUserToSPP = gql`
    mutation upUserTOSPP($userId: String!, $pVersion: String!) {
        updateUserTOSandPP(userId: $userId, ppVersion: $pVersion)
    }
`;

export const deleteLastPagesOpenedQuery = gql`
    mutation deleteUserLastPages($pk: String!) {
        updateUserLastPages(userId: $pk, lastPagesOpened: []) {
            url
            studyId
            siteId
        }
    }
`;

export const updateUserLastActiveDate = gql`
    mutation updateUserLastActiveDate {
        updateUserLastActiveDate
    }
`;

export const markPwdQuery = gql`
    mutation markPassword($pk: String!, $pwd: String!) {
        markPasswordUsedForUser(userId: $pk, passwdHash: $pwd)
    }
`;

export const checkPwdAvailableQuery = gql`
    query checkPassword($pk: String!, $pwd: String!, $activationCode: String) {
        checkPasswordAvailabile(userId: $pk, passwdHash: $pwd, activationCode: $activationCode)
    }
`;

export const getRemainingAttempts = gql`
    query getUserNumberOfLoginTriesBeforeLockout($userId: String!) {
        getUserNumberOfLoginTriesBeforeLockout(userId: $userId)
    }
`;

export const getStudySites = gql`
    query getStudySites($studyId: String!) {
        getStudy(pk: $studyId) {
            sites {
                pk
                sk
                displayName
                data
                version
                isDeleted
                siteIdentifier
            }
        }
    }
`;

type GetRoleAndSubscriber = {
    getUser: {
        currentUserSubscriber: {
            subscriberId: string;
            currentRoleId: string;
        };
        roleBClaim: {
            sk: string;
            fxId: string;
        };
        roleVClaim: {
            sk: string;
            fxId: string;
        };
    };
};

export const GetRoleAndSubscriberDocumentNode: TypedDocumentNode<GetRoleAndSubscriber> = gql`
    query GetRoleAndSubscriberData($pk: String) {
        getUser(pk: $pk, sk: "User") {
            currentUserSubscriber {
                subscriberId
                currentRoleId
            }
            roleBClaim {
                sk
                fxId
            }
            roleVClaim {
                sk
                fxId
            }
        }
    }
`;

type HealthCheckBackend = {
    getUser: {
        pk: string;
        sk: string;
    };
};

export const HealthCheckBackendQuery: TypedDocumentNode<HealthCheckBackend> = gql`
    query HealthCheckBackend($pk: String!) {
        getUser(pk: $pk, sk: "User") {
            pk
            sk
        }
    }
`;
